/**
 * Fetches a workshop's real, BFF-generated Analysis content (ADO #685/
 * #689) — the replacement for the static `WS4_ANALYSIS` fixture in
 * `analysis-content.ts`.
 *
 * Generation happens server-side (see
 * `services/bff/app/analysis_generation.py`) when a workshop's assets
 * change, and persists to `<workshopId>/generated/analysis.json` on the
 * existing `workshops` blob tier (ADO #686) — this hook just reads that
 * blob via the same `getJson` helper every other JSON-on-blob workshop
 * record uses. No new backend endpoint was needed: the existing
 * `GET /api/docs/{tier}/file` route already serves it.
 *
 * Returns `data: null` (not an error) both while nothing has been generated
 * yet and while the workshop backend is IndexedDB-only (`VITE_WORKSHOP_BACKEND`
 * off) — callers should fall back to the static WS4 fixture only for WS4, and
 * render an explicit empty state otherwise (ADO #685: no silent fallback to
 * fiction for a real workshop with no analysis yet).
 *
 * `data.chatTelemetry` is independently optional (ADO #689's `MC_CHAT_ANALYTICS`
 * flag) — present only when chat-interaction telemetry was captured and the
 * flag is on; the modal renders that section conditionally.
 */

import { useEffect, useState } from "react";
import { isWorkshopBffEnabled } from "@/env";
import { getJson } from "@/lib/docs-api";
import { useGeneratedWorkshopContent } from "@/stores/useGeneratedWorkshopContent";
import type { AnalysisContent } from "./analysis-content";

const WORKSHOPS_TIER = "workshops";

/** Tier-relative blob path for a workshop's generated Analysis — must
 *  match `generated_content_path(workshop_id, "analysis")` in the BFF
 *  exactly. */
export function generatedAnalysisPath(workshopId: string): string {
  return `${workshopId}/generated/analysis.json`;
}

export type GeneratedAnalysisState = {
  data: AnalysisContent | null;
  loading: boolean;
  error: boolean;
};

export function useGeneratedAnalysis(workshopId: string): GeneratedAnalysisState {
  const [state, setState] = useState<GeneratedAnalysisState>({
    data: null,
    loading: isWorkshopBffEnabled(),
    error: false,
  });

  useEffect(() => {
    if (!isWorkshopBffEnabled()) {
      // Local/seed mode has no server-side generation to read — the caller
      // falls back to the static fixture (WS4) or the empty state (others).
      setState({ data: null, loading: false, error: false });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: false }));
    getJson<AnalysisContent>(WORKSHOPS_TIER, generatedAnalysisPath(workshopId))
      .then((value) => {
        // Write-through to the synchronous cache the chat's context-injection
        // path reads (ADO #685 wrap-up) — see useGeneratedWorkshopContent.
        useGeneratedWorkshopContent.getState().setAnalysis(workshopId, value);
        if (!cancelled) setState({ data: value, loading: false, error: false });
      })
      .catch(() => {
        // A transient fetch failure isn't "no analysis" — surface it
        // distinctly so the modal doesn't quietly show an empty state for a
        // real error.
        if (!cancelled) setState({ data: null, loading: false, error: true });
      });
    return () => {
      cancelled = true;
    };
  }, [workshopId]);

  return state;
}
